import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel, Counter, Marquee } from "../components/primitives";
import {
  IMAGES, SOLUTIONS, PRODUCTS, WHY, BRANDS, CLIENTS, VOUCHER_CATS, WORK, INSIGHTS, STATS,
} from "../data/site";

const PageHero = ({ label, title, sub, img }) => (
  <section className="pt-[120px] md:pt-[150px] pb-14">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <SectionLabel>{label}</SectionLabel>
      <Reveal>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.95] max-w-4xl">{title}</h1>
      </Reveal>
      {sub && <Reveal delay={0.1}><p className="mt-6 text-lg text-neutral-600 max-w-2xl">{sub}</p></Reveal>}
      {img && (
        <Reveal delay={0.15}>
          <div className="mt-12 rounded-2xl overflow-hidden aspect-[21/9]">
            <img src={img} alt={label} className="w-full h-full object-cover" loading="eager" />
          </div>
        </Reveal>
      )}
    </div>
  </section>
);

const CTA = () => (
  <section className="py-20">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <div className="rounded-2xl bg-[#f4b41a] p-10 md:p-16 flex flex-wrap items-center justify-between gap-8">
        <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tight max-w-xl text-neutral-900">Ready to consolidate your requirements?</h2>
        <Link to="/inquire" className="group inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white font-semibold px-7 py-4 hover:bg-neutral-800 transition-colors">
          Inquire Now <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
        </Link>
      </div>
    </div>
  </section>
);

export const About = () => (
  <div data-testid="about-page">
    <PageHero label="About" title={<>A new culture of <span className="text-[#f4b41a]">corporate needs.</span></>}
      sub="VETAS is your one-stop solution vendor for everything — consolidating merchandise, gifting, rewards and engagement into a single, dependable partnership." img={IMAGES.teamMeeting} />
    <section className="py-16">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12">
        <Reveal><p className="font-display text-2xl md:text-3xl font-medium tracking-tight leading-snug">We focus on innovation and creativity to redefine how businesses approach organizational requirements — with tailored solutions aligned to each client's goals and values.</p></Reveal>
        <Reveal delay={0.1}><div className="space-y-5 text-neutral-600 text-lg leading-relaxed">
          <p>From cutting-edge merchandising strategies to personalized corporate gifting, we are committed to the success and satisfaction of every partnership.</p>
          <p>With in-house apparel manufacturing, a network of 100+ brands and pan-India delivery, VETAS handles multiple requirements through one relationship.</p>
        </div></Reveal>
      </div>
    </section>
    <section className="py-10">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-neutral-200 pt-12">
        {STATS.map((s) => (
          <Reveal key={s.label}><div><div className="font-display text-5xl md:text-6xl font-medium tracking-tighter"><Counter to={s.value} suffix={s.suffix} /></div><p className="mt-2 text-sm text-neutral-500 uppercase tracking-[0.15em]">{s.label}</p></div></Reveal>
        ))}
      </div>
    </section>
    <section className="py-16">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">Why partners choose us</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {WHY.map((w, i) => (
            <Reveal key={w.n} delay={(i % 3) * 0.06}>
              <div className="rounded-2xl border border-neutral-200 p-7 h-full hover:bg-[#f9f6f0] transition-colors">
                <span className="font-display text-3xl text-[#f4b41a] font-medium">{w.n}</span>
                <h3 className="font-display text-xl font-medium tracking-tight mt-3">{w.title}</h3>
                <p className="mt-2 text-neutral-600 text-sm">{w.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <CTA />
  </div>
);

export const Solutions = () => (
  <div data-testid="solutions-page">
    <PageHero label="Solutions" title={<>What can we <span className="text-[#f4b41a]">build for you?</span></>}
      sub="Seven ways VETAS turns corporate requirements into finished, delivered solutions — all through one partner." />
    <section className="pb-16">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 border-t border-neutral-200">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.n} delay={(i % 3) * 0.05}>
            <div className="group grid md:grid-cols-12 gap-6 items-center py-6 border-b border-neutral-200">
              <span className="md:col-span-1 font-display text-3xl text-[#f4b41a] font-medium">{s.n}</span>
              <div className="md:col-span-3 rounded-xl overflow-hidden aspect-[4/3]">
                <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <h3 className="md:col-span-4 font-display text-2xl md:text-3xl font-medium tracking-tight">{s.title}</h3>
              <p className="md:col-span-3 text-neutral-600">{s.desc}</p>
              <Link to="/inquire" className="md:col-span-1 justify-self-end"><ArrowUpRight className="text-neutral-300 group-hover:text-neutral-900 transition-colors" size={24} /></Link>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <CTA />
  </div>
);

export const Products = () => (
  <div data-testid="products-page">
    <PageHero label="Products" title={<>Breadth, <span className="text-[#f4b41a]">not clutter.</span></>}
      sub="An editorial showcase of what's possible — WEAR, GIFT, TECH, REWARD and SUSTAIN." />
    <section className="pb-20">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 space-y-5">
        {PRODUCTS.map((p, i) => (
          <Reveal key={p.tag} delay={(i % 2) * 0.08}>
            <div className={`grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-neutral-200 ${i % 2 ? "md:[direction:rtl]" : ""}`}>
              <div className="aspect-[4/3] md:aspect-auto overflow-hidden">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover min-h-[300px]" loading="lazy" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center [direction:ltr]">
                <span className="text-[#f4b41a] text-sm font-semibold tracking-[0.3em]">{p.tag}</span>
                <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">{p.title}</h3>
                <p className="mt-3 text-neutral-600 text-lg">{p.items}</p>
                <Link to="/inquire" className="mt-6 inline-flex items-center gap-2 font-semibold group w-fit">Request this <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></Link>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <CTA />
  </div>
);

const chip = (label) => (
  <span className="mx-2 inline-flex items-center rounded-full border border-neutral-200 bg-white px-6 py-3 text-lg md:text-xl font-display font-medium text-neutral-400 hover:text-neutral-900 transition-colors">{label}</span>
);

export const Network = () => (
  <div data-testid="network-page">
    <PageHero label="Our Network" title={<>A network built for <span className="text-[#f4b41a]">possibilities.</span></>}
      sub="Access to a large network of brands and manufacturers across every category — plus a voucher network catering to 400+ brands." />
    <section className="pb-8">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 border-y border-neutral-200 py-12">
        {[["100+", "Brand Associations"], ["400+", "Voucher Brands"], ["25+", "Corporate Clients"], ["25+", "States Covered"]].map(([v, l]) => (
          <Reveal key={l}><div><div className="font-display text-5xl md:text-6xl font-medium tracking-tighter text-[#f4b41a]">{v}</div><p className="mt-2 text-sm text-neutral-500 uppercase tracking-[0.15em]">{l}</p></div></Reveal>
        ))}
      </div>
    </section>
    <section className="py-8 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 mb-8"><h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">100+ brand associations</h2></div>
      <div className="space-y-4">
        <Marquee items={BRANDS.slice(0, 22)} dur="55s" render={chip} />
        <Marquee items={BRANDS.slice(20)} reverse dur="50s" render={chip} />
      </div>
    </section>
    <section className="py-16">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-8">Voucher & gift-card categories</h2>
        <div className="flex flex-wrap gap-3">
          {VOUCHER_CATS.map((c, i) => (
            <Reveal key={c} delay={(i % 6) * 0.04}><span className="rounded-full border border-neutral-200 px-5 py-3 text-neutral-700 hover:bg-[#f4b41a] hover:border-[#f4b41a] transition-colors">{c}</span></Reveal>
          ))}
        </div>
      </div>
    </section>
    <CTA />
  </div>
);

export const Work = () => (
  <div data-testid="work-page">
    <PageHero label="Work / Case Studies" title={<>From requirement <span className="text-[#f4b41a]">to reality.</span></>}
      sub="How we turn corporate requirements into delivered solutions. Detailed, named case studies are added as clients approve them." />
    <section className="pb-24">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 space-y-6">
        {WORK.map((w, i) => (
          <Reveal key={w.client} delay={(i % 2) * 0.06}>
            <div className={`grid lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden border border-neutral-200 bg-white ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="relative aspect-[4/3] lg:aspect-auto min-h-[280px] overflow-hidden group">
                <img src={w.img} alt={w.client} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <span className="absolute top-5 left-5 rounded-full bg-[#f4b41a] text-neutral-900 text-xs font-semibold px-4 py-2 [direction:ltr]">{w.cat}</span>
              </div>
              <div className="p-8 md:p-12 [direction:ltr]">
                <span className="font-display text-4xl font-medium text-neutral-200">0{i + 1}</span>
                <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-1">{w.client}</h3>
                <div className="mt-6 space-y-5">
                  {[["Requirement", w.requirement], ["Solution", w.solution], ["Outcome", w.outcome]].map(([h, body]) => (
                    <div key={h} className="grid grid-cols-[110px_1fr] gap-4 items-baseline border-t border-neutral-200 pt-4">
                      <span className="text-xs uppercase tracking-[0.15em] text-[#d99e12] font-semibold">{h}</span>
                      <p className="text-neutral-700 leading-relaxed">{body}</p>
                    </div>
                  ))}
                  <div className="grid grid-cols-[110px_1fr] gap-4 items-baseline border-t border-neutral-200 pt-4">
                    <span className="text-xs uppercase tracking-[0.15em] text-neutral-400 font-semibold">Scale</span>
                    <p className="text-neutral-700">{w.scale}</p>
                  </div>
                </div>
                <Link to="/inquire" className="mt-8 inline-flex items-center gap-2 font-semibold group w-fit">Start a similar project <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></Link>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal>
          <div className="rounded-2xl border border-dashed border-neutral-300 p-10 text-center">
            <p className="text-neutral-500">More detailed, named case studies with verified outcomes are being added.</p>
          </div>
        </Reveal>
      </div>
    </section>
    <CTA />
  </div>
);

export const Sustainability = () => (
  <div data-testid="sustainability-page">
    <PageHero label="Sustainability" title={<>Better products. <span className="text-[#f4b41a]">Better choices.</span></>}
      sub="Introducing our recycled-plastic apparel line — a blend of style, comfort and environmental responsibility." img={IMAGES.sustain1} />
    <section className="py-16">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">How our recycled fabric is made</h2>
        <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[["1", "Bottles collected & cleaned"], ["2", "Shredded into flakes"], ["3", "Melted into pellets"], ["4", "Extruded into yarn"], ["5", "Spun into fabric"]].map(([n, t], i) => (
            <Reveal key={n} delay={i * 0.06}>
              <div className="rounded-2xl bg-[#ebf4f1] border border-emerald-100 p-6 h-full">
                <span className="font-display text-4xl font-medium text-emerald-700">{n}</span>
                <p className="mt-3 text-emerald-900 font-medium">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <section className="py-16">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal><div>
          <SectionLabel>Social Impact</SectionLabel>
          <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Corporate giving that gives back.</h2>
          <p className="mt-5 text-neutral-600 text-lg leading-relaxed">We work with <strong className="text-neutral-900">Ashirwad</strong>, a unit of the North India Cerebral Palsy Association (NICPA), supporting therapy services and promoting products made by the children of the NGO through corporate gifting.</p>
        </div></Reveal>
        <Reveal delay={0.1}><img src={IMAGES.ngo2} alt="Ashirwad" className="rounded-2xl w-full aspect-[4/3] object-cover" loading="lazy" /></Reveal>
      </div>
    </section>
    <CTA />
  </div>
);

export const Insights = () => (
  <div data-testid="insights-page">
    <PageHero label="Insights" title={<>The VETAS <span className="text-[#f4b41a]">Playbook.</span></>}
      sub="Ideas on corporate gifting, employee engagement, channel programs and sustainable merchandise. Articles coming soon." />
    <section className="pb-24">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid md:grid-cols-3 gap-5">
        {INSIGHTS.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.08}>
            <div className="rounded-2xl bg-white border border-neutral-200 p-7 h-full hover:border-neutral-900 transition-colors">
              <div className="flex items-center justify-between mb-10">
                <span className="text-xs uppercase tracking-[0.2em] text-[#f4b41a] font-semibold">{a.cat}</span>
                <span className="text-xs text-neutral-400">{a.read} · Soon</span>
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight leading-snug">{a.title}</h3>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
    <CTA />
  </div>
);
